import {first, isUndefined, keyBy, last, mapValues} from 'lodash';

import {ScrollDirectionsEnum} from '../../../../../srcCommon/scroll/scrollerTypes';
import {CellsBoundary, CellsBoundaryBuilder} from '../../../../helpers/cellsHelpers';
import {
  ConversationMetaModel,
  hasMatchedQueriesConversationMetaModel,
} from '../../../../models/api/conversationMetaModel';
import {ConversationSearchResultModel} from '../../../../models/api/conversationSearchResultModel';
import {AsyncMoreStatusesEnum, ResourceMap} from '../../../appTypes';

/** Compute the more statuses and limits after an initial fetch. */
export function updateInitialMoreStatusesAndLimits<TItem, TId>(
  boundaryBuilder: CellsBoundaryBuilder<TItem, TId>,
  sortedItems: ReadonlyArray<TItem>,
  aroundIndex: number | undefined,
  limit: number,
) {
  const result = updateInitialMoreStatuses(sortedItems, aroundIndex, limit);
  return {
    ...result,
    topBoundary: buildBoundary(boundaryBuilder, result.moreStatusTop, first(sortedItems)),
    bottomBoundary: buildBoundary(boundaryBuilder, result.moreStatusBottom, last(sortedItems)),
  };
}

/** Compute the more statuses after an initial fetch. */
function updateInitialMoreStatuses<TItem>(
  sortedItems: ReadonlyArray<TItem>,
  aroundIndex: number | undefined,
  limit: number,
) {
  // If this request wasn't around an item, things are straightforward.
  if (isUndefined(aroundIndex)) {
    return {
      moreStatusTop: AsyncMoreStatusesEnum.ALL_LOADED,
      moreStatusBottom: computeMoreStatus(sortedItems.length, limit),
    };
  }

  // Conservative estimate of the maximum number of items returned before/after. The server typically does
  // beforeLimit := Math.ceil(limit / 2) and afterLimit := limit - beforeLimit.
  const aroundLimit = Math.floor(limit / 2);

  // If the item around which we fetched is not included, we can only guess based on the total count.
  const isAroundItemInBounds = aroundIndex >= 0 && aroundIndex < sortedItems.length;
  if (!isAroundItemInBounds) {
    const moreStatus = computeMoreStatus(sortedItems.length, aroundLimit);

    return {
      moreStatusTop: moreStatus,
      moreStatusBottom: moreStatus,
    };
  }

  // Conservative count of the items returned before/after. The server typically includes the "around item"
  // in the top half, but let's not assume.
  const countTop = aroundIndex + 1;
  const countBottom = sortedItems.length - aroundIndex;

  // If the item around which we fetched IS included, guess based on the before/after count.
  return {
    moreStatusTop: computeMoreStatus(countTop, aroundLimit),
    moreStatusBottom: computeMoreStatus(countBottom, aroundLimit),
  };
}

/** Compute the more status and limit after a fetch more. */
export function updateMoreStatusAndLimit<TItem, TId>(
  boundaryBuilder: CellsBoundaryBuilder<TItem, TId>,
  sortedItems: ReadonlyArray<TItem>,
  direction: ScrollDirectionsEnum,
  limit: number,
) {
  const status = computeMoreStatus(sortedItems.length, limit);
  const result = updateMoreStatus(direction, status);

  if (result.moreStatusTop) {
    return {
      ...result,
      topBoundary: buildBoundary(boundaryBuilder, result.moreStatusTop, first(sortedItems)),
    };
  }

  return {
    ...result,
    bottomBoundary: buildBoundary(boundaryBuilder, result.moreStatusBottom, last(sortedItems)),
  };
}

/** Compute the more status after a fetch more. */
export function updateMoreStatus(direction: ScrollDirectionsEnum, status: AsyncMoreStatusesEnum) {
  switch (direction) {
    case ScrollDirectionsEnum.TOP:
      return {moreStatusTop: status};

    default:
      return {moreStatusBottom: status};
  }
}

/** Find out if there's more to load. */
function computeMoreStatus(length: number, limit: number) {
  return length < limit || limit === 0 ? AsyncMoreStatusesEnum.ALL_LOADED : AsyncMoreStatusesEnum.HAS_MORE;
}

/** Build a boundary object from a single conversation. */
function buildBoundary<TItem, TId>(
  boundaryBuilder: CellsBoundaryBuilder<TItem, TId>,
  moreStatus: AsyncMoreStatusesEnum,
  item?: TItem,
): CellsBoundary<TId> | undefined {
  // If we can't load more in this direction, no need for a limit.
  if (moreStatus !== AsyncMoreStatusesEnum.HAS_MORE || !item) {
    return undefined;
  }

  // Otherwise, build it from the item.
  return boundaryBuilder(item);
}

export function computeMoreStatusForBoundary(
  boundary: CellsBoundary<unknown> | undefined,
): AsyncMoreStatusesEnum {
  return boundary ? AsyncMoreStatusesEnum.HAS_MORE : AsyncMoreStatusesEnum.ALL_LOADED;
}

/** Extract conversation matched queries from metas. */
export function extractConversationMatchedQueries(
  conversationMetas?: ReadonlyArray<ConversationMetaModel>,
): ResourceMap<ReadonlyArray<string>> {
  if (!conversationMetas || conversationMetas.length === 0) {
    return {};
  }

  // Filter conversationMetas containing matched queries.
  const filteredConversationMetas = conversationMetas.filter(hasMatchedQueriesConversationMetaModel);

  return mapValues(
    keyBy(filteredConversationMetas, (m) => m.conversationId),
    (m) => m.matchedQueries,
  );
}

export function extractConversationSearchResults(
  conversationSearchResults?: ReadonlyArray<ConversationSearchResultModel>,
): ResourceMap<ConversationSearchResultModel> | undefined {
  if (!conversationSearchResults) {
    return undefined;
  }

  return keyBy(conversationSearchResults, (m) => m.id);
}
